import React, { useEffect, useState, useRef } from 'react';
import { ConnectWebSocketForOverlay, DisconnectWebSocket, SetSocketMessageHandler, ClearSocketMessageHandler } from './../../../utilities/BL/Zones';
import { FinishPlayback, GetState } from './../../../utilities/BL/Sequence';

const SequenceScreen = ({ screenId = 1, isStandalone = true, payload = null }) => {
    const [connectionStatus, setConnectionStatus] = useState('idle');
    
    const [gameState, setGameState] = useState({
        phase: 'WAITING',
        sequence: [],
        playerStep: 0,
        isPlaying: false,
        matchId: ''
    });

    const [activePad, setActivePad] = useState(null); // ID del pad iluminado actualmente
    const [countdownNumber, setCountdownNumber] = useState(null);
    const [confettiList, setConfettiList] = useState([]);
    
    const isPlayingRef = useRef(false);
    const lastTappedRef = useRef(null);

    const COLOR_CLASSES = {
        0: 'pad-0',
        1: 'pad-1',
        2: 'pad-2',
        3: 'pad-3',
        4: 'pad-4',
        5: 'pad-5'
    };

    const CONFETTI_COLORS = ['#00ff41', '#ff003c', '#fffb00', '#00e1ff', '#b300ff', '#ff8800', '#ffffff'];

    const customStyles = `
        :root {
            --pad0: #00ff41; /* Green */
            --pad1: #ff003c; /* Red */
            --pad2: #fffb00; /* Yellow */
            --pad3: #00e1ff; /* Blue */
            --pad4: #b300ff; /* Purple */
            --pad5: #ff8800; /* Orange */
            --bg-dark: #0a0a0f;
        }

        .sequence-screen-body {
            margin: 0;
            padding: 0;
            width: 100vw;
            height: 100vh;
            background-color: var(--bg-dark);
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            font-family: 'Outfit', sans-serif;
            color: #fff;
            position: relative;
        }

        /* Neon dotted frame */
        .neon-frame {
            position: absolute;
            top: 20px;
            left: 20px;
            right: 20px;
            bottom: 20px;
            width: calc(100vw - 40px);
            height: calc(100vh - 40px);
            pointer-events: none;
            z-index: 10;
        }

        .neon-frame rect {
            width: calc(100% - 12px);
            height: calc(100% - 12px);
            x: 6px;
            y: 6px;
            fill: none;
            stroke: rgba(255, 255, 255, 0.05);
            stroke-width: 12px;
            stroke-dasharray: 0.1 35;
            stroke-linecap: round;
            transition: stroke 0.3s;
        }

        .neon-frame.active rect {
            animation: strobe-svg 0.5s infinite alternate;
        }

        .neon-frame.countdown rect {
            animation: countdown-pulse-svg 1s infinite alternate;
            stroke: rgba(255, 255, 255, 0.8);
        }

        .neon-frame.error rect {
            stroke: var(--pad1);
            filter: drop-shadow(0 0 20px var(--pad1));
            animation: slot-machine 1.5s linear infinite;
            stroke-width: 16px;
        }

        .neon-frame.win-crazy rect {
            animation: strobe-party 0.2s infinite alternate, slot-machine 0.5s linear infinite;
            stroke-width: 20px;
        }

        @keyframes slot-machine {
            to { stroke-dashoffset: -350; }
        }

        @keyframes strobe-party {
            0% { stroke: #00ff41; filter: drop-shadow(0 0 40px #00ff41); }
            33% { stroke: #00e1ff; filter: drop-shadow(0 0 40px #00e1ff); }
            66% { stroke: #b300ff; filter: drop-shadow(0 0 40px #b300ff); }
            100% { stroke: #fffb00; filter: drop-shadow(0 0 40px #fffb00); }
        }

        @keyframes strobe-svg {
            0% { stroke: rgba(255, 255, 255, 0.8); filter: drop-shadow(0 0 20px #fff); }
            100% { stroke: rgba(255, 255, 255, 0.2); }
        }

        @keyframes countdown-pulse-svg {
            0% { filter: drop-shadow(0 0 0px #fff); opacity: 0.3; }
            100% { filter: drop-shadow(0 0 35px #fff); opacity: 1; }
        }

        /* Grid */
        .grid-container {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-gap: 40px;
            width: 900px;
            height: 550px;
            z-index: 20;
        }

        .pad {
            border-radius: 35px;
            background: rgba(20, 20, 25, 0.9);
            border: 8px solid;
            transition: transform 0.15s, box-shadow 0.15s, background-color 0.15s;
            position: relative;
        }

        .pad-0 { border-color: rgba(0, 255, 65, 0.2); }
        .pad-1 { border-color: rgba(255, 0, 60, 0.2); }
        .pad-2 { border-color: rgba(255, 251, 0, 0.2); }
        .pad-3 { border-color: rgba(0, 225, 255, 0.2); }
        .pad-4 { border-color: rgba(179, 0, 255, 0.2); }
        .pad-5 { border-color: rgba(255, 136, 0, 0.2); }

        /* Active states */
        .pad-0.active { background-color: var(--pad0); box-shadow: 0 0 60px var(--pad0), inset 0 0 45px var(--pad0); border-color: #fff; transform: scale(1.06); }
        .pad-1.active { background-color: var(--pad1); box-shadow: 0 0 60px var(--pad1), inset 0 0 45px var(--pad1); border-color: #fff; transform: scale(1.06); }
        .pad-2.active { background-color: var(--pad2); box-shadow: 0 0 60px var(--pad2), inset 0 0 45px var(--pad2); border-color: #fff; transform: scale(1.06); }
        .pad-3.active { background-color: var(--pad3); box-shadow: 0 0 60px var(--pad3), inset 0 0 45px var(--pad3); border-color: #fff; transform: scale(1.06); }
        .pad-4.active { background-color: var(--pad4); box-shadow: 0 0 60px var(--pad4), inset 0 0 45px var(--pad4); border-color: #fff; transform: scale(1.06); }
        .pad-5.active { background-color: var(--pad5); box-shadow: 0 0 60px var(--pad5), inset 0 0 45px var(--pad5); border-color: #fff; transform: scale(1.06); }

        /* Overlays */
        .overlay-msg {
            position: absolute;
            font-size: 8rem;
            font-weight: 900;
            text-transform: uppercase;
            text-shadow: 0 0 30px rgba(0,0,0,0.9);
            z-index: 100;
            pointer-events: none;
            opacity: 0;
            transform: scale(0.5);
            transition: opacity 0.15s, transform 0.15s;
        }

        .overlay-msg.show {
            opacity: 1;
            transform: scale(1);
            animation: pop-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }

        .overlay-msg.countdown-text.show {
            font-size: 20rem;
            animation: countdown-pop 1s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }

        .color-win {
            color: var(--pad0);
            text-shadow: 0 0 40px var(--pad0), 0 0 100px #fff;
            animation: win-vibrate 0.25s infinite;
        }

        .color-lose {
            color: var(--pad1);
            text-shadow: 0 0 40px var(--pad1), 0 0 100px #fff;
            animation: lose-glitch 0.35s infinite;
        }

        .win-body-vibration {
            animation: body-vibrate 0.1s infinite;
        }

        @keyframes body-vibrate {
            0% { transform: translate(3px, 3px); }
            25% { transform: translate(-3px, -3px); }
            50% { transform: translate(3px, -3px); }
            75% { transform: translate(-3px, 3px); }
            100% { transform: translate(0, 0); }
        }

        @keyframes win-vibrate {
            0% { transform: scale(1.1) rotate(0deg); text-shadow: 0 0 50px var(--pad0), 0 0 100px #fff; }
            50% { transform: scale(1.1) rotate(1deg); text-shadow: 0 0 60px var(--pad3), 0 0 120px #fff; }
            100% { transform: scale(1.1) rotate(-1deg); text-shadow: 0 0 50px var(--pad0), 0 0 100px #fff; }
        }

        @keyframes lose-glitch {
            0% { transform: scale(1) translateX(-10px) skewX(5deg); }
            50% { transform: scale(1.1) translateX(10px) skewX(-5deg); color: #fff; }
            100% { transform: scale(1) translateX(-5px) skewX(2deg); }
        }

        @keyframes pop-in {
            0% { transform: scale(0.5); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
        }

        @keyframes countdown-pop {
            0% { transform: scale(0.6); opacity: 0; filter: blur(10px); }
            50% { transform: scale(1.1); opacity: 1; filter: blur(0); }
            100% { transform: scale(1.4); opacity: 0; filter: blur(10px); }
        }

        /* CSS 3D Confetti Pieces */
        .confetti-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            pointer-events: none;
            z-index: 5;
            overflow: hidden;
        }

        .confetti-piece {
            position: absolute;
            width: var(--size);
            height: calc(var(--size) * 0.45);
            background-color: var(--color);
            top: -20px;
            left: var(--left);
            opacity: 0;
            transform-style: preserve-3d;
            animation: fall-confetti var(--duration) linear var(--delay) infinite;
        }

        @keyframes fall-confetti {
            0% {
                transform: translate3d(0, 0, 0) rotate3d(1, 1, 1, 0deg);
                opacity: 0;
            }
            10% {
                opacity: 1;
            }
            90% {
                opacity: 1;
            }
            100% {
                transform: translate3d(var(--drift), 105vh, 0) rotate3d(1.5, 2, 1, var(--rotation));
                opacity: 0;
            }
        }
    `;

    // Conectar WebSocket
    useEffect(() => {
        if (!isStandalone && payload) {
            handleIncomingMessage(payload);
            return;
        }

        const connectWS = async () => {
            const overlayType = `sequence:screen:${screenId}`;
            await ConnectWebSocketForOverlay(
                overlayType,
                (status) => setConnectionStatus(status),
                (toastMsg) => console.log(`[SequenceScreen WS] ${toastMsg}`)
            );
            SetSocketMessageHandler(handleIncomingMessage);
        };

        connectWS();

        // Cargar estado inicial por HTTP por seguridad
        fetchInitialState();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, [screenId, isStandalone, payload]);

    const fetchInitialState = async () => {
        const res = await GetState(screenId);
        if (res && res.success && res.data) {
            // Si ya está corriendo, no lo interrumpimos con layout
            if (res.data.phase !== 'PLAYBACK') {
                setGameState(res.data);
            }
        }
    };

    // Reaccionar a toques del jugador en tiempo real
    useEffect(() => {
        if (gameState.phase === 'PLAYER_INPUT' && gameState.lastTappedColorId !== null) {
            const tapKey = `${gameState.matchId}-${gameState.playerStep}-${gameState.lastTappedColorId}`;
            if (lastTappedRef.current !== tapKey) {
                lastTappedRef.current = tapKey;
                // Flashear el botón presionado
                triggerFlash(gameState.lastTappedColorId);
            }
        }

        // Generar confeti si gana
        if (gameState.phase === 'WON') {
            generateConfetti();
        } else {
            setConfettiList([]);
        }
    }, [gameState.phase, gameState.lastTappedColorId, gameState.playerStep, gameState.matchId]);

    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        const type = message.type.toLowerCase();
        const payloadData = message.payload || {};

        if (type === 'sequence.layout') {
            // Iniciar ciclo de reproducción
            if (payloadData.sequence && payloadData.sequence.length > 0) {
                setGameState(payloadData);
                playSequence(payloadData.sequence, payloadData.matchId, payloadData.playbackSpeedMs || 500);
            }
        } else if (type === 'sequence.update') {
            // Actualizar estado general
            setGameState(payloadData);
        } else if (type === 'sequence.reset') {
            // Reiniciar
            setGameState({
                phase: 'WAITING',
                sequence: [],
                playerStep: 0,
                isPlaying: false,
                matchId: ''
            });
            setActivePad(null);
            setCountdownNumber(null);
            isPlayingRef.current = false;
        }
    };

    // Lanzar destello de un botón
    const triggerFlash = (colorId, duration = 200) => {
        setActivePad(colorId);
        setTimeout(() => {
            setActivePad(prev => prev === colorId ? null : prev);
        }, duration);
    };

    // Ciclo de reproducción de secuencia
    const playSequence = async (sequence, matchId, speed) => {
        if (isPlayingRef.current) return;
        isPlayingRef.current = true;

        // 1. Conteo 3, 2, 1
        for (let i = 3; i >= 1; i--) {
            setCountdownNumber(i);
            await new Promise(r => setTimeout(r, 1000));
        }
        setCountdownNumber(null);

        // Gap de espera antes del Simon
        await new Promise(r => setTimeout(r, 500));

        // 2. Destellos del Simon
        for (let i = 0; i < sequence.length; i++) {
            triggerFlash(sequence[i], speed - 150 > 200 ? speed - 150 : 250);
            await new Promise(r => setTimeout(r, speed));
        }

        isPlayingRef.current = false;

        // 3. Notificar fin de reproducción
        await FinishPlayback(screenId, matchId);
    };

    // Generar confeti CSS 3D (30 elementos)
    const generateConfetti = () => {
        const list = [];
        for (let i = 0; i < 35; i++) {
            const startLeft = Math.random() * 100;
            const size = Math.random() * 12 + 10; // 10px a 22px
            const duration = Math.random() * 1.5 + 2.5; // 2.5s a 4s
            const delay = Math.random() * 1.5; // 0s a 1.5s delay
            const drift = Math.random() * 20 - 10; // -10vw a 10vw
            const rotation = Math.random() * 720 + 360; // 360deg a 1080deg
            const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];

            list.push({
                id: i,
                style: {
                    '--left': `${startLeft}vw`,
                    '--size': `${size}px`,
                    '--color': color,
                    '--duration': `${duration}s`,
                    '--delay': `${delay}s`,
                    '--drift': `${drift}vw`,
                    '--rotation': `${rotation}deg`
                }
            });
        }
        setConfettiList(list);
    };

    const isWon = gameState.phase === 'WON';
    const isLost = gameState.phase === 'LOST';
    const isPlayback = gameState.phase === 'PLAYBACK';

    const DEFAULT_BUTTONS = [
        { id: 0, color: '#00ff41', imageUrl: '', label: 'VERDE' },
        { id: 1, color: '#ff003c', imageUrl: '', label: 'ROJO' },
        { id: 2, color: '#fffb00', imageUrl: '', label: 'AMARILLO' },
        { id: 3, color: '#00e1ff', imageUrl: '', label: 'AZUL' },
        { id: 4, color: '#b300ff', imageUrl: '', label: 'MORADO' },
        { id: 5, color: '#ff8800', imageUrl: '', label: 'NARANJA' }
    ];
    const buttonsToUse = (gameState.buttons && gameState.buttons.length > 0) ? gameState.buttons : DEFAULT_BUTTONS;

    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: buttonsToUse.length <= 4 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gridGap: '40px',
        width: '900px',
        height: '550px',
        zIndex: 20
    };

    return (
        <>
            <div className={`sequence-screen-body ${isWon ? 'win-body-vibration' : ''}`}>
                <style>{customStyles}</style>

                <img src="/img/page/adsl-five.png" style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', width: '450px' }} />

                {/* Neon Border */}
                <svg 
                    className={`neon-frame ${
                        isPlayback ? 'active' :
                        countdownNumber ? 'countdown' :
                        isWon ? 'win-crazy' :
                        isLost ? 'error' : ''
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect x="0" y="0" rx="30" ry="30"></rect>
                </svg>

                {/* Simon Grid */}
                <div style={gridStyle}>
                    {buttonsToUse.map((btn) => {
                        const isActive = activePad === btn.id;
                        const padStyle = {
                            borderRadius: '35px',
                            background: isActive ? btn.color : 'rgba(20, 20, 25, 0.9)',
                            border: '8px solid',
                            borderColor: isActive ? '#fff' : `${btn.color}33`,
                            boxShadow: isActive ? `0 0 60px ${btn.color}, inset 0 0 45px ${btn.color}` : 'none',
                            transform: isActive ? 'scale(1.06)' : 'none',
                            transition: 'transform 0.15s, box-shadow 0.15s, background-color 0.15s',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                        };
                        return (
                            <div 
                                key={btn.id} 
                                style={padStyle}
                            >
                                {btn.imageUrl && (
                                    <img 
                                        src={getMediaUrl(btn.imageUrl)} 
                                        alt={btn.label} 
                                        style={{ 
                                            width: '110%', //'100%', 
                                            height: '120%', //'100%', 
                                            objectFit: 'cover',
                                            filter: isActive ? 'brightness(1.2)' : 'none',
                                            opacity: isActive ? 1 : 0.6
                                        }} 
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Countdown Overlay */}
                {countdownNumber && (
                    <div className="overlay-msg countdown-text show">
                        {countdownNumber}
                    </div>
                )}

                {/* Game Result Overlays */}
                {isWon && (
                    <div className="overlay-msg show color-win">
                        ¡GANASTE!
                    </div>
                )}

                {isLost && (
                    <div className="overlay-msg show color-lose">
                        INCORRECTO
                    </div>
                )}

                {/* CSS 3D Confetti Elements */}
                {isWon && confettiList.length > 0 && (
                    <div className="confetti-container">
                        {confettiList.map(item => (
                            <div 
                                key={item.id} 
                                className="confetti-piece" 
                                style={item.style}
                            />
                        ))}
                    </div>
                )}

            </div>

            <p className='FooterCredits' style={{ position: 'fixed', bottom: '0', left: '50%', transform: 'translateX(-50%)', fontSize: '30px', color: '#fff' }}>Hecho con ♥ por Interaktia | 2026</p>
        </>
    );
};

export { SequenceScreen };
